/**
 * ManagedConnection - Individual WebSocket connection handler
 * 
 * Handles connection lifecycle, reconnection logic, message queuing,
 * and error handling for a single WebSocket connection.
 */

import { ConnectionType, ConnectionStatus } from './WebSocketConnectionManager';

export class ManagedConnection {
  private ws: WebSocket | null = null;
  private status: ConnectionStatus = ConnectionStatus.DISCONNECTED;
  private messageQueue: { data: any; timestamp: number }[] = [];
  private reconnectAttempts = 0;
  private reconnectTimer: NodeJS.Timeout | null = null;
  private lastConnectTime = 0;
  private manualDisconnect = false;
  private recentMessageHashes: Map<string, number> = new Map(); // For deduplication
  private readonly MESSAGE_DEDUP_WINDOW_MS = 2000; // 2 second deduplication window
  
  // Configuration
  private readonly MAX_RECONNECT_ATTEMPTS = 5;
  private readonly MIN_RECONNECT_DELAY = 1000;
  private readonly MAX_RECONNECT_DELAY = 30000;
  private readonly MESSAGE_QUEUE_MAX_SIZE = 100;
  private readonly MESSAGE_QUEUE_MAX_AGE = 60000; // 1 minute
  private readonly RATE_LIMIT_WINDOW = 1000; // 1 second minimum between connections
  
  constructor(
    private type: ConnectionType,
    private getUrl: () => Promise<string>,
    private notifySubscribers: (event: 'message' | 'status', data: any) => void
  ) {
    console.log(`[ManagedConnection] Created for type: ${type}`);
  }
  
  /**
   * Connect to WebSocket
   */
  async connect(): Promise<void> {
    // Don't connect if already connected or connecting
    if (this.status === ConnectionStatus.CONNECTED || 
        this.status === ConnectionStatus.CONNECTING) {
      console.log(`[ManagedConnection:${this.type}] Already ${this.status}, skipping connect`);
      return;
    }
    
    // In development with React StrictMode, prevent rapid double connections
    if (process.env.NODE_ENV === 'development') {
      const now = Date.now();
      if (this.lastConnectTime && now - this.lastConnectTime < 100) {
        console.log(`[ManagedConnection:${this.type}] Skipping rapid reconnect in dev mode (StrictMode)`)
        return;
      }
    }
    
    // Rate limiting to prevent rapid reconnection attempts
    const now = Date.now();
    const timeSinceLastConnect = now - this.lastConnectTime;
    if (timeSinceLastConnect < this.RATE_LIMIT_WINDOW) {
      const waitTime = this.RATE_LIMIT_WINDOW - timeSinceLastConnect;
      console.warn(`[ManagedConnection:${this.type}] Rate limiting - waiting ${waitTime}ms`);
      setTimeout(() => this.connect(), waitTime);
      return;
    }
    this.lastConnectTime = now;
    
    // Reset manual disconnect flag
    this.manualDisconnect = false;
    
    try {
      this.updateStatus(ConnectionStatus.CONNECTING);
      
      // Get WebSocket URL
      const url = await this.getUrl();
      if (!url) {
        throw new Error('Failed to get WebSocket URL');
      }
      
      console.log(`[ManagedConnection:${this.type}] Connecting to WebSocket...`);
      
      // Create WebSocket
      this.ws = new WebSocket(url);
      this.setupEventHandlers();
      
    } catch (error) {
      console.error(`[ManagedConnection:${this.type}] Connection failed:`, error);
      this.handleConnectionError(error);
    }
  }
  
  /**
   * Setup WebSocket event handlers
   */
  private setupEventHandlers(): void {
    if (!this.ws) return;
    
    this.ws.onopen = () => {
      console.log(`[ManagedConnection:${this.type}] Connected successfully`);
      this.updateStatus(ConnectionStatus.CONNECTED);
      this.reconnectAttempts = 0;
      this.flushMessageQueue();
    };
    
    this.ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        this.notifySubscribers('message', message);
      } catch (error) {
        console.error(`[ManagedConnection:${this.type}] Failed to parse message:`, error);
      }
    };
    
    this.ws.onerror = (error) => {
      console.error(`[ManagedConnection:${this.type}] WebSocket error:`, error);
      this.handleConnectionError(error);
    };
    
    this.ws.onclose = (event) => {
      console.log(`[ManagedConnection:${this.type}] Connection closed: code=${event.code}, reason=${event.reason}`);
      this.ws = null;
      
      // Check if this was a manual disconnect
      if (this.manualDisconnect) {
        console.log(`[ManagedConnection:${this.type}] Manual disconnect, not reconnecting`);
        this.updateStatus(ConnectionStatus.DISCONNECTED);
        return;
      }
      
      // Check if auth failed (401)
      if (event.code === 1008 || event.reason?.includes('401') || event.reason?.includes('Unauthorized')) {
        console.error(`[ManagedConnection:${this.type}] Authentication failed`);
        this.updateStatus(ConnectionStatus.AUTH_FAILED);
        return; // Don't reconnect on auth failure
      }
      
      // Check for normal closure
      if (event.code === 1000) {
        console.log(`[ManagedConnection:${this.type}] Normal closure`);
        this.updateStatus(ConnectionStatus.DISCONNECTED);
        return;
      }
      
      this.updateStatus(ConnectionStatus.DISCONNECTED);
      this.scheduleReconnect();
    };
  }
  
  /**
   * Schedule reconnection with exponential backoff
   */
  private scheduleReconnect(): void {
    // Don't reconnect if manually disconnected
    if (this.manualDisconnect) {
      console.log(`[ManagedConnection:${this.type}] Manual disconnect, skipping reconnect`);
      return;
    }
    
    if (this.reconnectAttempts >= this.MAX_RECONNECT_ATTEMPTS) {
      console.error(`[ManagedConnection:${this.type}] Max reconnection attempts reached`);
      this.updateStatus(ConnectionStatus.ERROR);
      return;
    }
    
    // Exponential backoff with jitter
    const baseDelay = Math.min(
      this.MIN_RECONNECT_DELAY * Math.pow(2, this.reconnectAttempts),
      this.MAX_RECONNECT_DELAY
    );
    // Add jitter (±25% of base delay)
    const jitter = baseDelay * 0.25 * (Math.random() * 2 - 1);
    const delay = Math.round(baseDelay + jitter);
    
    console.log(`[ManagedConnection:${this.type}] Scheduling reconnect in ${delay}ms (attempt ${this.reconnectAttempts + 1}/${this.MAX_RECONNECT_ATTEMPTS})`);
    this.reconnectAttempts++;
    this.updateStatus(ConnectionStatus.RECONNECTING);
    
    // Clear existing timer if any
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
    }
    
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }
  
  /**
   * Generate a hash for message deduplication
   */
  private generateMessageHash(message: any): string {
    const messageStr = typeof message === 'string' ? message : JSON.stringify(message);
    // Simple hash based on message content and type
    let hash = 0;
    for (let i = 0; i < messageStr.length; i++) {
      const char = messageStr.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return `${this.type}_${hash}_${messageStr.length}`;
  }

  /**
   * Clean up old message hashes
   */
  private cleanupMessageHashes(): void {
    const now = Date.now();
    for (const [hash, timestamp] of this.recentMessageHashes.entries()) {
      if (now - timestamp > this.MESSAGE_DEDUP_WINDOW_MS) {
        this.recentMessageHashes.delete(hash);
      }
    }
  }

  /**
   * Send a message through the WebSocket
   */
  send(message: any): void {
    const msgId = `MSG-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    // Check for duplicate message
    const messageHash = this.generateMessageHash(message);
    const now = Date.now();
    
    // Clean up old hashes
    this.cleanupMessageHashes();
    
    // Check if this message was recently sent
    if (this.recentMessageHashes.has(messageHash)) {
      const lastSentTime = this.recentMessageHashes.get(messageHash)!;
      if (now - lastSentTime < this.MESSAGE_DEDUP_WINDOW_MS) {
        console.warn(`[ManagedConnection:${this.type}] [${msgId}] Duplicate message detected, skipping send (last sent ${now - lastSentTime}ms ago)`);
        return;
      }
    }
    
    if (this.ws?.readyState === WebSocket.OPEN) {
      try {
        const messageStr = typeof message === 'string' ? message : JSON.stringify(message);
        console.log(`[ManagedConnection:${this.type}] [${msgId}] Sending message:`, {
          messageType: message.type,
          messageLength: messageStr.length,
          preview: messageStr.substring(0, 200)
        });
        this.ws.send(messageStr);
        // Record successful send for deduplication
        this.recentMessageHashes.set(messageHash, now);
        console.log(`[ManagedConnection:${this.type}] [${msgId}] Message sent successfully`);
      } catch (error) {
        console.error(`[ManagedConnection:${this.type}] [${msgId}] Failed to send message:`, error);
        this.queueMessage(message);
      }
    } else {
      console.log(`[ManagedConnection:${this.type}] [${msgId}] Connection not ready (state: ${this.ws?.readyState}), queuing message`);
      this.queueMessage(message);
    }
  }
  
  /**
   * Queue message for later sending
   */
  private queueMessage(message: any): void {
    // Add timestamp for TTL
    const queuedMessage = {
      data: message,
      timestamp: Date.now()
    };
    
    this.messageQueue.push(queuedMessage);
    
    // Limit queue size
    if (this.messageQueue.length > this.MESSAGE_QUEUE_MAX_SIZE) {
      const removed = this.messageQueue.shift();
      console.warn(`[ManagedConnection:${this.type}] Message queue full, dropping oldest message`);
    }
    
    console.log(`[ManagedConnection:${this.type}] Message queued (queue size: ${this.messageQueue.length})`);
  }
  
  /**
   * Flush queued messages
   */
  private flushMessageQueue(): void {
    if (this.messageQueue.length === 0) return;
    
    const now = Date.now();
    let sentCount = 0;
    let droppedCount = 0;
    
    // Filter and send valid messages
    this.messageQueue = this.messageQueue.filter(item => {
      // Check if message is too old
      if (now - item.timestamp > this.MESSAGE_QUEUE_MAX_AGE) {
        droppedCount++;
        return false; // Remove old messages
      }
      
      // Try to send the message
      try {
        if (this.ws?.readyState === WebSocket.OPEN) {
          const messageStr = typeof item.data === 'string' ? item.data : JSON.stringify(item.data);
          this.ws.send(messageStr);
          sentCount++;
          return false; // Remove sent messages
        }
      } catch (error) {
        console.error(`[ManagedConnection:${this.type}] Failed to send queued message:`, error);
      }
      
      return true; // Keep message in queue if not sent
    });
    
    console.log(`[ManagedConnection:${this.type}] Queue flush: sent=${sentCount}, dropped=${droppedCount}, remaining=${this.messageQueue.length}`);
  }
  
  /**
   * Disconnect the WebSocket
   */
  disconnect(): void {
    console.log(`[ManagedConnection:${this.type}] Disconnecting...`);
    
    // Set manual disconnect flag to prevent reconnection
    this.manualDisconnect = true;
    
    // Clear reconnect timer
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    
    // Close WebSocket if open
    if (this.ws) {
      try {
        this.ws.close(1000, 'Normal closure');
      } catch (error) {
        console.error(`[ManagedConnection:${this.type}] Error closing WebSocket:`, error);
      }
      this.ws = null;
    }
    
    // Clear message queue
    this.messageQueue = [];
    this.reconnectAttempts = 0;
    
    this.updateStatus(ConnectionStatus.DISCONNECTED);
  }
  
  /**
   * Force reconnect (reset attempts and connect)
   */
  reconnect(): void {
    console.log(`[ManagedConnection:${this.type}] Manual reconnect requested`);
    
    // Reset state
    this.reconnectAttempts = 0;
    this.manualDisconnect = false;
    
    // Clear any pending reconnect timer
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    
    // Disconnect current connection if any
    if (this.ws) {
      try {
        this.ws.close(1000, 'Manual reconnect');
      } catch (error) {
        console.error(`[ManagedConnection:${this.type}] Error closing for reconnect:`, error);
      }
      this.ws = null;
    }
    
    // Connect immediately
    this.connect();
  }
  
  /**
   * Get current connection status
   */
  getStatus(): ConnectionStatus {
    return this.status;
  }
  
  /**
   * Update connection status and notify subscribers
   */
  private updateStatus(status: ConnectionStatus): void {
    if (this.status === status) return;
    
    console.log(`[ManagedConnection:${this.type}] Status: ${this.status} -> ${status}`);
    this.status = status;
    this.notifySubscribers('status', status);
  }
  
  /**
   * Handle connection errors
   */
  private handleConnectionError(error: any): void {
    // Check for auth failures
    const errorMessage = error?.message || error?.toString() || '';
    if (errorMessage.includes('401') || errorMessage.includes('Unauthorized')) {
      console.error(`[ManagedConnection:${this.type}] Authentication error`);
      this.updateStatus(ConnectionStatus.AUTH_FAILED);
      return;
    }
    
    this.updateStatus(ConnectionStatus.ERROR);
    
    // Don't schedule reconnect if manually disconnected
    if (!this.manualDisconnect) {
      this.scheduleReconnect();
    }
  }
  
  /**
   * Get debug information
   */
  getDebugInfo(): {
    type: ConnectionType;
    status: ConnectionStatus;
    reconnectAttempts: number;
    queueSize: number;
    isConnected: boolean;
  } {
    return {
      type: this.type,
      status: this.status,
      reconnectAttempts: this.reconnectAttempts,
      queueSize: this.messageQueue.length,
      isConnected: this.ws?.readyState === WebSocket.OPEN
    };
  }
}